import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BZ_DIALOG_DATA, BzDialogRef} from '@bringoz/ui';
import {Subject, timer} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import * as _ from 'lodash';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {BatchRecoveryService} from '../../../../batch/services/batch-recovery.service';
import {RecoveryActionEnum} from '../../../../batch/enums/recovery-action.enum';
import {BatchTypeEnum} from '../../../../batch/enums/batch-type.enum';
import {BatchRecoveryOptionsModel} from '../../../../batch/models/batch-recovery-options.model';
import {BatchProcessTrackingModel} from '../../../../batch/models/batch-process-tracking.model';
import {ProcessTrackingStatusEnum} from '../../../../commons/types/process-tracking-status.enum';
import {TimekeepingHttpService} from '../../services/timekeeping-http.service';
import {PayrollExportRecoveryDialogData} from '../../../types/timekeeping-dialog.type';

const POLLING_INTERVAL_MS = 3000;

/**
 * Follows a payroll export while it runs, and offers Retry, Skip or Cancel when it failed in parts or stalled.
 * Resolves true once the export ended, false when it was cancelled, or nothing when closed without a choice.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timekeeping-payroll-recovery-dialog',
  templateUrl: './timekeeping-payroll-recovery-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimekeepingPayrollRecoveryDialogComponent implements OnInit {

  readonly data = inject<PayrollExportRecoveryDialogData>(BZ_DIALOG_DATA);
  private readonly ref = inject<BzDialogRef<boolean>>(BzDialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly timekeepingHttpService = inject(TimekeepingHttpService);
  private readonly batchRecoveryService = injectLegacy<BatchRecoveryService>('batchRecoveryService');

  readonly recoveryActions = RecoveryActionEnum;
  readonly canRecover = this.batchRecoveryService.canRecover();
  readonly process = signal<BatchProcessTrackingModel | null>(null);
  readonly options = signal<BatchRecoveryOptionsModel | null>(null);
  readonly loading = signal(false);
  readonly pendingAction = signal<RecoveryActionEnum | null>(null);
  readonly processing = signal(true);
  private readonly pollStop$ = new Subject<void>();

  readonly stalled = computed(() => this.options()?.stalled ?? this.process()?.stalled ?? this.data.stalled);
  readonly canRetry = computed(() => _.includes(this.options()?.actions, RecoveryActionEnum.RETRY));
  readonly canSkip = computed(() => _.includes(this.options()?.actions, RecoveryActionEnum.SKIP));
  readonly canCancel = computed(() => _.includes(this.options()?.actions, RecoveryActionEnum.CANCEL));
  readonly progress = computed(() => {
    const totals = _.reduce(this.process()?.progressMap, (acc, value) => ({
      total: acc.total + (value?.total || 0),
      done: acc.done + (value?.created || 0) + (value?.existed || 0) + (value?.updated || 0) + (value?.failed || 0)
    }), {total: 0, done: 0});
    return totals.total ? Math.round(totals.done / totals.total * 100) : 0;
  });
  // Nothing left to choose - no permission, no allowed action, or the options failed to load.
  readonly closable = computed(() => !this.canRecover || (!this.loading() && _.isEmpty(this.options()?.actions)));

  ngOnInit() {
    this.followExport();
  }

  /** Retry runs at once; Skip and Cancel cannot be undone, so they ask first. */
  onAction(action: RecoveryActionEnum) {
    if (action !== RecoveryActionEnum.RETRY && this.pendingAction() !== action) {
      this.pendingAction.set(action);
      return;
    }

    this.pendingAction.set(null);
    this.loading.set(true);
    this.batchRecoveryService.execute(action, this.data.batchId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (action === RecoveryActionEnum.CANCEL) {
            this.ref.close(false);
            return;
          }
          this.loading.set(false);
          this.options.set(null);
          this.followExport();
        },
        error: () => this.loadOptions()
      });
  }

  onBack() {
    this.pendingAction.set(null);
  }

  onClose() {
    this.ref.close();
  }

  private followExport() {
    this.processing.set(true);

    timer(0, POLLING_INTERVAL_MS)
      .pipe(
        switchMap(() => this.timekeepingHttpService.getBulkProcess()),
        takeUntil(this.pollStop$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (this.batchRecoveryService.isCandidate(response, [BatchTypeEnum.ExportTimeCardsPayroll])) {
            this.pollStop$.next();
            this.process.set(response as BatchProcessTrackingModel);
            this.processing.set(false);
            if (this.canRecover) {
              this.loadOptions();
            }
          } else if (!response || response.processStatus !== ProcessTrackingStatusEnum.IN_PROGRESS) {
            this.pollStop$.next();
            this.ref.close(true);
          } else {
            this.process.set(response as BatchProcessTrackingModel);
          }
        },
        error: () => this.ref.close(true)
      });
  }

  private loadOptions() {
    this.loading.set(true);
    this.batchRecoveryService.getOptions(this.data.batchId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.options.set(options);
          this.loading.set(false);
        },
        error: () => this.loading.set(false)
      });
  }
}
